import type { SelectOption } from "@leabankab/component-library";
import type { CountryCode } from "@/config/country-config";
import { getCountryRules } from "@/config/country-rules";
import {
	RefinanceContextDebt,
	RefinanceDebt,
	RefinancePayloadDebt,
} from "@/features/refinance/types";
import { toAmountOrZero } from "@/shared/amount";
import { formatGiro, inferGiroType } from "./giro";
import { matchOptionValue } from "./match-option-value";

export interface DebtSeedOptions {
	typeOptions: SelectOption[];
	bankOptions: SelectOption[];
	country: CountryCode;
}

/** A credit check row with no balance, such as an unused card, has nothing to refinance. */
export const hasReportedBalance = (
	debt: Pick<RefinanceContextDebt, "amount">
): boolean => toAmountOrZero(debt.amount) > 0;

/**
 * Turns the task's debts into form rows. `type`, `description` and `bank` are
 * the register's own wording, so they are matched onto option keys; a
 * description that matches nothing is kept as the row's label. FLOW sends a
 * UC or PCR row without the register flag and sets it to false on a debt the
 * customer added, which is not capped. A task opened again after a submit
 * carries the payment details already sent, and a debt with both starts saved.
 * Register debts come first: they cannot be removed, so the form keys their
 * seed by index.
 */
export const seedDebtsFromContext = (
	debts: RefinanceContextDebt[],
	{ typeOptions, bankOptions, country }: DebtSeedOptions
): RefinanceDebt[] => {
	const { reportedDebtFlag, redemptionPayment } = getCountryRules(country);

	const rows = debts.filter(hasReportedBalance).map((debt): RefinanceDebt => {
		const describedType = matchOptionValue(debt.description, typeOptions);
		const description =
			describedType || matchOptionValue(debt.type, typeOptions);
		const amount = toAmountOrZero(debt.amount);
		const iban = debt.iban?.trim() ?? "";
		const loanNumber = debt.loanNumber?.trim() ?? "";
		const fromRegister = debt[reportedDebtFlag] !== false;

		return {
			bank: matchOptionValue(debt.bank, bankOptions),
			amount,
			originalAmount: fromRegister ? amount : undefined,
			description,
			sourceLabel: describedType
				? undefined
				: debt.description.trim() || undefined,
			iban,
			loanNumber,
			internalRedemption: debt.internalRedemption,
			giroType:
				iban && redemptionPayment === "giro-ocr"
					? inferGiroType(iban)
					: "bankgiro",
			isReported: true,
			fromRegister,
			saved: !!debt.internalRedemption || (!!iban && !!loanNumber),
		};
	});

	return [
		...rows.filter((row) => row.fromRegister),
		...rows.filter((row) => !row.fromRegister),
	];
};

export const emptyManualDebt = (): RefinanceDebt => ({
	bank: "",
	amount: undefined,
	description: "",
	iban: "",
	loanNumber: "",
	internalRedemption: false,
	giroType: "bankgiro",
	isReported: false,
	fromRegister: false,
});

// Only a giro number is normalised; an IBAN is sent as typed.
const toPayloadIban = (
	{ iban = "", giroType }: RefinanceDebt,
	country: CountryCode
) =>
	getCountryRules(country).redemptionPayment === "giro-ocr"
		? formatGiro(iban, giroType ?? "bankgiro")
		: iban;

export const toRefinancePayloadDebts = (
	debts: RefinanceDebt[],
	country: CountryCode
): RefinancePayloadDebt[] => {
	const { reportedDebtFlag } = getCountryRules(country);

	return debts.map((debt) => ({
		description: debt.description ?? "",
		bank: debt.bank ?? "",
		amount: toAmountOrZero(debt.amount),
		monthlyAmount: 0,
		iban: debt.internalRedemption ? "" : toPayloadIban(debt, country),
		loanNumber: debt.internalRedemption ? "" : (debt.loanNumber ?? ""),
		internalRedemption: debt.internalRedemption,
		[reportedDebtFlag]: debt.fromRegister === true,
	}));
};
